import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Check, Minus } from "lucide-react";

import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow } from "@/components/marketing/Layout";
import {
  getLocalizedTopic,
  topicAlternates,
  TOPIC_LOCALES,
  TOPIC_PATH,
} from "@/lib/localized-topics.functions";
import { getTopic, TOPICS, type Topic } from "@/lib/topics";

const SITE = "https://realitytwin.io";

/**
 * Topic pages in Spanish, French, Italian, Russian and Chinese.
 *
 * The translation happens in the loader, which runs on the server during SSR.
 * TanStack serialises the result into the page payload, so the client hydrates
 * from the same strings the server rendered — no hydration mismatch, and the
 * HTML is in the target language from the first byte.
 *
 * English lives at /topics and German at /de/themen; both are hand-authored and
 * are excluded here so the same content never has two URLs in one language.
 */
export const Route = createFileRoute("/$lang/topics/$slug")({
  loader: async ({ params }) => {
    const lang = params.lang.split("-")[0];
    if (!(TOPIC_LOCALES as readonly string[]).includes(lang)) throw notFound();
    if (!getTopic(params.slug)) throw notFound();
    const data = await getLocalizedTopic({ data: { lang, slug: params.slug } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const lang = params.lang.split("-")[0];
    const topic = loaderData?.topic;
    const self = `${SITE}/${lang}/${TOPIC_PATH[lang] ?? "topics"}/${params.slug}`;

    if (!topic)
      return buildSeo({
        path: `/${params.lang}/topics/${params.slug}`,
        title: "Reality Twin",
        description: "How agentic AI actually works in B2B.",
        noindex: true,
      });

    const seo = buildSeo({
      path: `/${lang}/topics/${params.slug}`,
      title: topic.metaTitle,
      description: topic.metaDescription,
      type: "article",
    });

    return {
      ...seo,
      links: [
        ...seo.links,
        // Every language version of this topic. All of these URLs exist and
        // each genuinely serves its own language.
        ...topicAlternates(params.slug, SITE).map((a) => ({
          rel: "alternate",
          hrefLang: a.lang,
          href: a.href,
        })),
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/topics/${params.slug}` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.metaTitle,
            description: topic.metaDescription,
            inLanguage: lang,
            author: { "@type": "Organization", name: "Reality Twin" },
            publisher: { "@type": "Organization", name: "Reality Twin", url: SITE },
            mainEntityOfPage: self,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: lang,
            mainEntity: topic.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: LocalizedTopicPage,
});

function LocalizedTopicPage() {
  const { lang, slug } = Route.useParams();
  const { topic, chrome } = Route.useLoaderData() as { topic: Topic; chrome: string[] };
  const [
    cInPractice,
    cRealityTitle,
    cRealityLead,
    cWorks,
    cDoesNot,
    cToRoadmap,
    cCtaTitle,
    cCtaBody,
    cFaq,
    cMore,
    cInEnglish,
    cCtaCheck,
    cCtaStart,
  ] = chrome;

  // Sibling topics in the same language. Titles stay English here because the
  // list of other topics is not part of the translated payload — linking with a
  // known-English label beats fetching six more translations per page view.
  const others = TOPICS.filter((x) => x.slug !== slug);

  return (
    <MarketingLayout>
      <article className="py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow>{topic.name}</Eyebrow>
          <h1 className="mt-4 font-serif text-[36px] sm:text-[56px] leading-[1.03] tracking-[-0.03em]">
            {topic.name}
          </h1>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted-foreground">{topic.heroLead}</p>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-12 space-y-5">
          {topic.thesis.map((p, i) => (
            <p key={i} className="text-[17px] leading-[1.7]">
              {p}
            </p>
          ))}
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            {cInPractice}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {topic.inPractice.map((s, i) => (
              <div key={i} className="rounded-2xl hairline bg-card p-5">
                <div className="font-medium">{s.title}</div>
                <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            {cRealityTitle}
          </h2>
          <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">{cRealityLead}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl hairline bg-card p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                {cWorks}
              </div>
              <ul className="mt-3 space-y-2.5">
                {topic.reality.works.map((w, i) => (
                  <li key={i} className="flex gap-2 text-[14px] leading-[1.55]">
                    <Check
                      className="h-4 w-4 shrink-0 mt-0.5 text-[oklch(0.62_0.14_168)]"
                      strokeWidth={2}
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl hairline bg-cream p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                {cDoesNot}
              </div>
              <ul className="mt-3 space-y-2.5">
                {topic.reality.doesNot.map((d, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-[14px] leading-[1.55] text-muted-foreground"
                  >
                    <Minus className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Link
            to="/roadmap"
            className="mt-4 inline-flex items-center gap-1.5 text-[14px] hover:opacity-70"
          >
            {cToRoadmap}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="rounded-2xl hairline bg-card p-6 sm:p-8">
            <h2 className="font-serif text-[24px] sm:text-[28px] tracking-[-0.02em]">
              {cCtaTitle}
            </h2>
            <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">{cCtaBody}</p>
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-2">
              <Link
                to="/twin-check"
                className="inline-flex justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
              >
                {cCtaCheck}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/auth"
                className="inline-flex justify-center items-center rounded-full hairline bg-background px-5 py-3 text-[14px] font-medium hover:bg-cream transition"
              >
                {cCtaStart}
              </Link>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">{cFaq}</h2>
          <div className="mt-6 divide-y divide-border hairline rounded-2xl bg-card">
            {topic.faqs.map((f, i) => (
              <div key={i} className="p-5">
                <div className="font-medium">{f.q}</div>
                <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
            {cMore}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/$lang/topics/$slug"
                params={{ lang, slug: o.slug }}
                className="rounded-full hairline bg-card px-3.5 py-1.5 text-[13px] hover:bg-cream transition"
              >
                {o.name}
              </Link>
            ))}
          </div>
          <Link
            to="/topics/$slug"
            params={{ slug }}
            className="mt-2 inline-block text-[13px] text-muted-foreground hover:text-foreground"
          >
            {cInEnglish} →
          </Link>
        </div>
      </article>
    </MarketingLayout>
  );
}
